import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MLSDetailResponse } from "../api/mls";
import { getPropertyDetails } from "../api/mls";
import { SavePropertyButton } from "../components/property/SavePropertyButton";
import { SqueezePageOverlay } from "../components/auth/SqueezePageOverlay";
import {
  useAuthUser,
  useIsAuthenticated,
  useAuthLoading,
  useHasMissingContact,
  useFirebaseUser,
} from "@/hooks/auth/useAuth";
import { useTenant } from "../contexts/TenantContext";
import { TenantConfig } from "../types/tenant";
import { logger } from "../utils/logger";
import { useURLParams } from "../contexts/URLParamsContext";
import { analyticsService } from "../services/analyticsService";
import ComplianceFooter from "../components/features/search/ComplianceFooter";
import ErrorDisplay from "../components/common/error/ErrorDisplay";
import { formatPhone } from "../utils/formatPhone";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Loader,
  ArrowLeft,
  Search,
  Phone,
  Mail,
} from "lucide-react";
import { PropertyBasicInfo } from "../components/property/PropertyDetail/PropertyBasicInfo";
import { PropertyDetailsGrid } from "../components/property/PropertyDetail/PropertyDetailsGrid";
import { ResponsiveContainer } from "../components/shared/responsive";
import { useMLSCompliance } from "../hooks/mls/useMLSCompliance";
import { useTeamMembers } from "../hooks/team/useTeamMembers";
import styles from "../components/property/PropertyDetail/PropertyDetail.module.css";
import placeholderImage from "/assets/images/place-holder.webp";
import noPhotosImage from "/assets/images/no-photos.webp";
import { formatCamelCase } from "../utils/formatText";
import { getOptimizedImageUrl } from "../utils/imageUtils";
import {
  resolvePropertyContactPhone,
  resolvePropertyContactEmail,
} from "../utils/contactUtils";

export const PropertyDetail: React.FC = () => {
  const { listingKey } = useParams<{ listingKey: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useTenant() as { config: TenantConfig };
  const { params: urlParams, updateParams, appendParamsToUrl } = useURLParams();
  const organizationId = config?.id || "";

  // Fetch the team roster only when there's an agent to resolve (?agent=);
  // otherwise the whole-roster fetch is wasted (teamMember ends up null).
  const { data: teamMembers } = useTeamMembers(
    urlParams.agent ? organizationId : ""
  );

  // Find the team member that matches the agent parameter
  const teamMember =
    teamMembers?.find((member) => member.email === urlParams.agent) || null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [propertyData, setPropertyData] = useState<
    MLSDetailResponse["data"]["listing"] | null
  >(null);
  const [timestamp, setTimestamp] = useState<string | undefined>(undefined);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showSqueezePage, setShowSqueezePage] = useState(false);
  const [bypassSqueeze, setBypassSqueeze] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const authLoading = useAuthLoading();
  const firebaseUser = useFirebaseUser();
  const user = useAuthUser();
  const hasMissingContact = useHasMissingContact();
  const [viewChecked, setViewChecked] = useState(false);

  // MLS compliance logo, shown beside the MLS#
  const mlsComplianceQueries = useMLSCompliance(
    propertyData?.mlsBoardCode ? [propertyData.mlsBoardCode] : [],
  );
  const mlsLogo = mlsComplianceQueries[0]?.data?.data?.mlsLogo;

  // The URLParamsProvider automatically handles URL parameters and campaign tracking
  // No additional logging or tracking needed here

  // Show squeeze page for non-authenticated users
  useEffect(() => {
    // Don't make any decisions while either loading state is active
    if (loading || authLoading) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const noSqueeze = searchParams.get("no_squeeze");

    // ?no_squeeze=true bypasses the squeeze overlay in every env (staging, UAT,
    // and production). Used by marketing/agent links that take a visitor
    // straight to the property without forcing the lead-capture form.
    const shouldBypass = noSqueeze === "true";

    // Update bypass state
    setBypassSqueeze(shouldBypass);

    // Don't show squeeze page if bypass is active
    if (shouldBypass) {
      setShowSqueezePage(false);
      return;
    }

    // Show squeeze page only when we're certain user is not authenticated
    if (!isAuthenticated) {
      setShowSqueezePage(true);
    } else {
      setShowSqueezePage(false);
    }
  }, [
    loading,
    isAuthenticated,
    authLoading,
    location.search,
    firebaseUser,
    user,
    bypassSqueeze,
    showSqueezePage,
  ]);

  useEffect(() => {
    const fetchPropertyDetails = async () => {
      if (!listingKey) {
        setError("Listing ID is required");
        return;
      }

      try {
        //Starting here TEMPT
        // Extract query parameters from URL
        const searchParams = new URLSearchParams(location.search);
        const tenantId = searchParams.get("tenant_id") || undefined;
        const mlsOsn = searchParams.get("mls_osn") || undefined;

        // Pass query parameters to getPropertyDetails
        const response = await getPropertyDetails(listingKey, tenantId, mlsOsn);

        //>>>> TEMP ORIG: const response = await getPropertyDetails(listingKey);

        logger.debug("Property details response", { response });

        if (!response.data) {
          throw new Error("Property details not found");
        }

        setPropertyData(response.data.listing);
        setTimestamp(response.timestamp);

        // Track property view event
        const listing = response.data.listing;
        analyticsService.trackEvent({
          tenantId: config?.id || tenantId || "unknown",
          orgId: config?.orgData?.id,
          eventType: "interaction",
          category: "Property",
          action: "View Property Details",
          label: listing.listingKey,
          value: listing.listPriceLow
            ? parseFloat(listing.listPriceLow)
            : undefined,
          data: {
            propertyId: listing.listingKey,
            mlsName: listing.mlsBoardCode,
            mlsBoardCode: listing.mlsBoardCode,
            address: listing.address?.unparsedAddress,
            city: listing.address?.city,
            state: listing.address?.stateOrProvince,
            zipCode: listing.address?.zipCode,
            listPrice: listing.listPriceLow
              ? parseFloat(listing.listPriceLow)
              : undefined,
            beds: listing.property?.bedroomsTotal,
            baths: listing.property?.bathroomsTotal,
            sqft: listing.property?.livingArea
              ? parseFloat(listing.property.livingArea)
              : undefined,
            propertyType: listing.property?.propertyType,
            propertySubType: listing.property?.propertySubType,
            listingStatus: listing.standardStatus,
            yearBuilt: listing.property?.yearBuilt,
            lotSize: listing.property?.lotSizeSquareFeet,
            searchQuery: urlParams.query,
            source: urlParams.source || document.referrer,
          },
        });
      } catch (err) {
        logger.error("Error fetching property", { error: err });
        const errorMessage =
          err instanceof Error
            ? err.message
            : typeof err === "object" && err && "message" in err
              ? String(err.message)
              : "Failed to load property details";

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyDetails();
  }, [listingKey, location.search]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(price);
  };

  const primaryColor = config?.branding?.colors?.primary || "#000000";

  if (loading || authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorDisplay
        message="Oops! This property seems to be playing hide and seek. Let's find another one!"
        colors={
          config?.branding?.colors || {
            accent: "#28a745",
            background: "#ffffff",
            text: "#202124",
          }
        }
        buttonText="Search Again"
        buttonIcon={<Search className="w-4 h-4 mr-2" />}
        onButtonClick={() => navigate("/search")}
      />
    );
  }

  if (!propertyData) {
    return (
      <ErrorDisplay
        message="Oops! This property seems to be playing hide and seek. Let's find another one!"
        colors={
          config?.branding?.colors || {
            accent: "#28a745",
            background: "#ffffff",
            text: "#202124",
          }
        }
        buttonText="Search Again"
        buttonIcon={<Search className="w-4 h-4 mr-2" />}
        onButtonClick={() => navigate("/search")}
      />
    );
  }

  // Generate photo URLs based on media data, respecting MLS display rules
  const photos = (() => {
    // Check MLS display rules
    const displayRules = propertyData.displayRules || {};
    const mustRemovePhotos = displayRules.mustRemovePhotos || false;
    const mustRemovePrimaryPhoto = displayRules.mustRemovePrimaryPhoto || false;

    // If primary photo must be removed, use the no-photos placeholder
    if (mustRemovePrimaryPhoto) {
      return [noPhotosImage];
    }

    const allPhotos = [];

    // Add primary photo if it exists
    if (propertyData.primaryPhotoUrl) {
      allPhotos.push(propertyData.primaryPhotoUrl);
    }

    // Add all media photos if not restricted by MLS rules
    if (!mustRemovePhotos) {
      const mediaPhotos =
        propertyData.media?.map(
          (photo: { mediaURL: string }) => photo.mediaURL,
        ) || [];

      // Combine all photos, removing duplicates (in case primary photo is also in media)
      const uniquePhotos = [...new Set([...allPhotos, ...mediaPhotos])];
      return uniquePhotos.length > 0 ? uniquePhotos : [placeholderImage];
    }

    // If mustRemovePhotos is true, only return the primary photo
    return allPhotos.length > 0 ? allPhotos : [placeholderImage];
  })();

  const hasPhotos = photos.length > 0;
  const hasSinglePhoto = photos.length === 1;
  const hasMultiplePhotos = photos.length > 1;

  const nextPhoto = () => {
    if (hasPhotos && hasMultiplePhotos) {
      setCurrentPhotoIndex((prev) =>
        prev === photos.length - 1 ? 0 : prev + 1,
      );
    }
  };

  const prevPhoto = () => {
    if (hasPhotos && hasMultiplePhotos) {
      setCurrentPhotoIndex((prev) =>
        prev === 0 ? photos.length - 1 : prev - 1,
      );
    }
  };

  const propertyType =
    propertyData.property?.propertySubType ||
    propertyData.property?.propertyType ||
    "Property";
  const city = propertyData.address?.city || "";
  const state = propertyData.address?.stateOrProvince || "";

  // Generate features from property details
  const features: string[] = [
    propertyData.property?.propertySubType,
    `${propertyData.property?.bedroomsTotal} beds`,
    `${propertyData.property?.bathroomsTotal} baths`,
    `${propertyData.property?.livingArea} sq ft`,
    propertyData.property?.hasPool ? "Pool" : undefined,
    propertyData.property?.hasBasement ? "Basement" : undefined,
    propertyData.property?.garageSpaces
      ? `${propertyData.property.garageSpaces} Car Garage`
      : undefined,
    propertyData.property?.stories
      ? `${propertyData.property.stories} Story`
      : undefined,
    propertyData.property?.view ? propertyData.property.view : undefined,
  ].filter((feature): feature is string => Boolean(feature));

  const handleSqueezePageClose = () => {
    setShowSqueezePage(false);

    // Check if page was opened in a new tab from another page
    if (window.opener) {
      // If opened via window.open (from search page), close the tab
      window.close();
    } else {
      // If directly navigated to, redirect to search page
      navigate("/search");
    }
  };

  const handleSqueezePageSuccess = (isExistingUser: boolean) => {
    setShowSqueezePage(false);
    setViewChecked(false);
  };

  // Render different layouts based on the number of photos
  const renderGalleryContent = () => {
    if (hasSinglePhoto) {
      return (
        <div
          className="w-full h-full relative flex items-center justify-center bg-gray-100"
          style={{
            backgroundImage: `url(${getOptimizedImageUrl(photos[0], 1200)})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
          aria-label={`${propertyType} in ${city}`}
        />
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 h-full relative">
        {/* Main photo - takes 2/3 of the width on desktop */}
        <div className="md:col-span-2 h-full relative">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url(${getOptimizedImageUrl(
                photos[currentPhotoIndex],
                1200,
              )})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            aria-label={`${propertyType} in ${city} - Photo ${
              currentPhotoIndex + 1
            }`}
          />

          {/* Mobile view: Show "+X photos" button */}
          {photos.length > 1 && (
            <div className="absolute bottom-4 right-4 md:hidden z-20">
              <button
                className="px-4 py-2 bg-white rounded-full text-black font-medium text-sm cursor-pointer shadow-md hover:bg-gray-100"
                onClick={() => setShowLightbox(true)}
              >
                +{photos.length - 1} photos
              </button>
            </div>
          )}
        </div>

        {/* Right column with additional photos - only visible on desktop */}
        <div className="hidden md:flex md:flex-col gap-2 h-full">
          {photos.length > 1 && (
            <div
              className="h-1/2 relative cursor-pointer"
              style={{
                backgroundImage: `url(${getOptimizedImageUrl(
                  photos[
                    currentPhotoIndex === 0
                      ? 1
                      : (currentPhotoIndex + 1) % photos.length
                  ],
                  600,
                )})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              onClick={() =>
                setCurrentPhotoIndex(
                  currentPhotoIndex === 0
                    ? 1
                    : (currentPhotoIndex + 1) % photos.length,
                )
              }
              aria-label={`${propertyType} in ${city} - Additional Photo 1`}
            />
          )}

          {photos.length > 2 && (
            <div
              className="h-1/2 relative cursor-pointer"
              style={{
                backgroundImage: `url(${getOptimizedImageUrl(
                  photos[
                    currentPhotoIndex === 0
                      ? 2
                      : (currentPhotoIndex + 2) % photos.length
                  ],
                  600,
                )})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              onClick={() =>
                setCurrentPhotoIndex(
                  currentPhotoIndex === 0
                    ? 2
                    : (currentPhotoIndex + 2) % photos.length,
                )
              }
              aria-label={`${propertyType} in ${city} - Additional Photo 2`}
            >
              {/* Desktop view: Show "+X photos" button on the last thumbnail */}
              {photos.length > 3 && (
                <div className="absolute bottom-4 right-4 z-20">
                  <button
                    className="px-4 py-2 bg-white rounded-full text-black font-medium text-sm cursor-pointer shadow-md hover:bg-gray-100"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering the parent onClick
                      setShowLightbox(true);
                    }}
                  >
                    +{photos.length - 3} photos
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Resolve the banner's contact phone/email once. Either can be absent (no
  // team member, no configured org contact) — render no link in that case
  // rather than an invalid `tel:undefined` / `mailto:undefined`.
  const bannerContactPhone = resolvePropertyContactPhone({
    teamMember,
    orgData: config?.orgData,
  });
  const bannerContactEmail = resolvePropertyContactEmail({
    teamMember,
    orgData: config?.orgData,
  });

  return (
    <div className="relative">
      {/* Quick Contact Banner */}
      {(bannerContactPhone || bannerContactEmail) && (
        <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-50 border-b border-gray-200 py-2 xl:py-3 px-4">
          <div className="mx-auto max-w-7xl flex items-center justify-center gap-3 sm:gap-6">
            <span className="text-sm sm:text-base font-medium text-gray-700">
              Questions about this property?
            </span>
            <div className="flex items-center gap-3 sm:gap-4">
              {bannerContactPhone && (
                <a
                  href={`tel:${bannerContactPhone}`}
                  className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold hover:opacity-75 transition-opacity"
                  style={{ color: primaryColor }}
                >
                  <Phone className="w-4 h-4" />
                  <span>Call</span>
                </a>
              )}
              {bannerContactPhone && bannerContactEmail && (
                <span className="text-gray-300">|</span>
              )}
              {bannerContactEmail && (
                <a
                  href={`mailto:${bannerContactEmail}`}
                  className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold hover:opacity-75 transition-opacity"
                  style={{ color: primaryColor }}
                >
                  <Mail className="w-4 h-4" />
                  <span>Email</span>
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        className={`${styles.fadeIn} ${
          !isAuthenticated && !bypassSqueeze ? "blur-md" : ""
        }`}
      >
        {/* MLS and Listing Information - WillametteValley only (shown above image) */}
        {propertyData.mlsBoardCode === "WillametteValley" && (
          <div className="">
            <div className="mx-auto xl:max-w-7xl xl:px-6">
              <div className="py-4 px-4 flex flex-col lg:flex-row justify-between items-start lg:items-center">
                <div className="flex flex-col">
                  {propertyData.mlsBoardCode?.toLowerCase() === "nwmls" ? (
                    <>
                      <div className="flex items-center">
                        <span className="text-sm md:text-base mr-2">
                          <span className={styles.propertyInfoLabel}>
                            Source:
                          </span>{" "}
                          <span className={styles.propertyInfoValue}>
                            {"NWMLS"}
                          </span>
                          ,{" "}
                          <span className={styles.propertyInfoLabel}>
                            MLS#:
                          </span>{" "}
                          <span className={styles.propertyInfoValue}>
                            {propertyData.mlsNumber
                              ? propertyData.mlsNumber.replace(/[a-zA-Z]/g, "")
                              : ""}
                          </span>
                        </span>

                        {mlsLogo && (
                          <img
                            src={mlsLogo}
                            alt="NWMLS logo"
                            className="h-6 ml-2"
                          />
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center">
                      <span
                        className={`text-sm md:text-base ${styles.propertyInfoLabel}`}
                      >
                        MLS#:
                      </span>
                      <span
                        className={`text-sm md:text-base ${styles.propertyInfoValue}`}
                      >
                        {propertyData.mlsNumber}
                      </span>
                      {mlsLogo && (
                        <img
                          src={mlsLogo}
                          alt={`${propertyData.mlsBoardCode} logo`}
                          className="h-4 md:h-6 ml-2 object-contain"
                        />
                      )}
                    </div>
                  )}
                </div>

                <div className="text-left lg:text-right">
                  {/* Listing Courtesy Information */}
                  <div className="sm:leading-[20px]">
                    <div className="flex flex-col sm:flex-row sm:items-center">
                      <span className={styles.propertyInfoLabel}>
                        Listing Courtesy of:
                      </span>{" "}
                      <span className={styles.propertyInfoValue}>
                        {propertyData.listingOffice?.name ||
                          propertyData.listingOffice?.ListOfficeName ||
                          ""}
                        {propertyData.listAgent?.listagentfullname && (
                          <> - {propertyData.listAgent.listagentfullname}</>
                        )}
                      </span>
                    </div>

                    {/* Agent contact info */}
                    <div className="flex flex-wrap gap-2">
                      {propertyData.listAgent?.listagentemail && (
                        <a
                          href={`mailto:${propertyData.listAgent.listagentemail}`}
                          className={`${styles.propertyInfoValue} underline`}
                        >
                          {propertyData.listAgent.listagentemail}
                        </a>
                      )}
                      {(() => {
                        const isMREIS =
                          (propertyData.mlsBoardCode as string) === "MREIS";
                        const attributionContact =
                          propertyData.listAgent?.attributioncontact;
                        const preferredPhone =
                          propertyData.listAgent?.listagentprefferedphone;
                        const officePhone =
                          propertyData.listAgent?.listagentofficephone;

                        const phoneToDisplay = isMREIS
                          ? attributionContact || officePhone || preferredPhone
                          : preferredPhone || attributionContact || officePhone;

                        return phoneToDisplay ? (
                          <a
                            href={`tel:${phoneToDisplay}`}
                            className={`${styles.propertyInfoValue} underline`}
                          >
                            {formatPhone(phoneToDisplay)}
                          </a>
                        ) : null;
                      })()}
                    </div>
                  </div>

                  {/* Buyer Agent Information */}
                  {(propertyData.standardStatus?.toLowerCase() === "closed" ||
                    propertyData.standardStatus?.toLowerCase() === "sold") &&
                    propertyData.buyerAgent &&
                    (propertyData.buyerAgent.buyeragentfullname ||
                      propertyData.buyerAgent.buyeragentmlsid ||
                      propertyData.buyerAgent.buyeragentofficephone) && (
                      <div className="text-sm md:text-base">
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className={styles.propertyInfoLabel}>
                            Bought with:
                          </span>{" "}
                          <span className={styles.propertyInfoValue}>
                            {propertyData.buyerAgent.buyeragentfullname}
                            {propertyData.buyerAgent.buyerofficename &&
                              ` - ${propertyData.buyerAgent.buyerofficename}`}
                          </span>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Hero Section with Main Image */}
        {hasPhotos && (
          <ResponsiveContainer
            className={`!py-0 px-0 md:px-6${propertyData.mlsBoardCode !== "WillametteValley" ? " mt-4" : ""}`}
          >
            <div className={styles.imageGallery}>
              <div className={styles.mainImage}>
                <button
                  className={styles.backButton}
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.opener) {
                      // If opened in new tab, close it
                      window.close();
                    } else if (
                      document.referrer &&
                      document.referrer.includes(window.location.host)
                    ) {
                      // If navigated from our site, use normal navigation
                      navigate(-1);
                    } else {
                      // If opened directly or pasted URL, go to search with URL parameters
                      window.location.href = appendParamsToUrl("/search");
                    }
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Search
                </button>

                {renderGalleryContent()}

                <span className={styles.photoCount}>
                  {currentPhotoIndex + 1} / {photos.length}
                </span>

                {/* Only show navigation controls if there are multiple photos */}
                {hasMultiplePhotos && (
                  <div className={styles.imageControls}>
                    <button className={styles.imageButton} onClick={prevPhoto}>
                      <ChevronLeft className="w-7 h-7 md:w-10 md:h-10" />
                    </button>
                    <button className={styles.imageButton} onClick={nextPhoto}>
                      <ChevronRight className="w-7 h-7 md:w-10 md:h-10" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </ResponsiveContainer>
        )}

        {/* MLS and Listing Courtesy - shown below image for all non-WillametteValley boards */}
        {propertyData.mlsBoardCode !== "WillametteValley" && (
            <div className="border-t border-gray-100 mx-auto xl:max-w-7xl xl:px-6">
              <div className="py-4 px-4 flex flex-col lg:flex-row justify-between items-start lg:items-center">
                <div className="flex flex-col">
                  {propertyData.mlsBoardCode?.toLowerCase() === "nwmls" ? (
                    <>
                      <div className="flex items-center">
                        <span className="text-sm md:text-base mr-2">
                          <span className={styles.propertyInfoLabel}>
                            Source:
                          </span>{" "}
                          <span className={styles.propertyInfoValue}>
                            {"NWMLS"}
                          </span>
                          ,{" "}
                          <span className={styles.propertyInfoLabel}>
                            MLS#:
                          </span>{" "}
                          <span className={styles.propertyInfoValue}>
                            {propertyData.mlsNumber
                              ? propertyData.mlsNumber.replace(/[a-zA-Z]/g, "")
                              : ""}
                          </span>
                        </span>

                        {mlsLogo && (
                          <img
                            src={mlsLogo}
                            alt="NWMLS logo"
                            className="h-6 ml-2"
                          />
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center">
                      <span
                        className={`text-sm md:text-base ${styles.propertyInfoLabel}`}
                      >
                        MLS#:
                      </span>
                      <span
                        className={`text-sm md:text-base ${styles.propertyInfoValue}`}
                      >
                        {propertyData.mlsNumber}
                      </span>
                      {mlsLogo && (
                        <img
                          src={mlsLogo}
                          alt={`${propertyData.mlsBoardCode} logo`}
                          className="h-4 md:h-6 ml-2 object-contain"
                        />
                      )}
                    </div>
                  )}
                </div>

                {propertyData.mlsBoardCode !== "Bright" && (
                <div className="text-left lg:text-right">
                  {/* Listing Courtesy Information */}
                  <div className="sm:leading-[20px]">
                    <div className="flex flex-col sm:flex-row sm:items-center">
                      <span className={styles.propertyInfoLabel}>
                        Listing Courtesy of:
                      </span>{" "}
                      <span className={styles.propertyInfoValue}>
                        {propertyData.listingOffice?.name ||
                          propertyData.listingOffice?.ListOfficeName ||
                          ""}
                        {propertyData.listAgent?.listagentfullname && (
                          <> - {propertyData.listAgent.listagentfullname}</>
                        )}
                      </span>
                    </div>

                    {/* Agent contact info */}
                    <div className="flex flex-wrap gap-2">
                      {propertyData.listAgent?.listagentemail && (
                        <a
                          href={`mailto:${propertyData.listAgent.listagentemail}`}
                          className={`${styles.propertyInfoValue} underline`}
                        >
                          {propertyData.listAgent.listagentemail}
                        </a>
                      )}
                      {(() => {
                        const isMREIS = propertyData.mlsBoardCode === "MREIS";
                        const attributionContact =
                          propertyData.listAgent?.attributioncontact;
                        const preferredPhone =
                          propertyData.listAgent?.listagentprefferedphone;
                        const officePhone =
                          propertyData.listAgent?.listagentofficephone;

                        const phoneToDisplay = isMREIS
                          ? attributionContact || officePhone || preferredPhone
                          : preferredPhone || attributionContact || officePhone;

                        return phoneToDisplay ? (
                          <a
                            href={`tel:${phoneToDisplay}`}
                            className={`${styles.propertyInfoValue} underline`}
                          >
                            {formatPhone(phoneToDisplay)}
                          </a>
                        ) : null;
                      })()}
                    </div>
                  </div>

                  {/* Buyer Agent Information */}
                  {(propertyData.standardStatus?.toLowerCase() === "closed" ||
                    propertyData.standardStatus?.toLowerCase() === "sold") &&
                    propertyData.buyerAgent &&
                    (propertyData.buyerAgent.buyeragentfullname ||
                      propertyData.buyerAgent.buyeragentmlsid ||
                      propertyData.buyerAgent.buyeragentofficephone) && (
                      <div className="text-sm md:text-base">
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className={styles.propertyInfoLabel}>
                            Bought with:
                          </span>{" "}
                          <span className={styles.propertyInfoValue}>
                            {propertyData.buyerAgent.buyeragentfullname}
                            {propertyData.buyerAgent.buyerofficename &&
                              ` - ${propertyData.buyerAgent.buyerofficename}`}
                          </span>
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        )}

        <ResponsiveContainer>
          {/* Header Information */}
          <div className={styles.header}>
            <h1
              className={`${styles.title} text-xl xl:text-[28px] !font-semibold`}
            >{`${propertyType} in ${city || ""}${
              state ? `, ${state}` : ""
            }`}</h1>
            <p
              className={`text-sm lg:text-lg xl:text-xl font-normal text-gray-800 ${styles.location} `}
            >
              {propertyData.internetAddressDisplayYN === false
                ? `${propertyData.address?.city || ""}, ${
                    propertyData.address?.stateOrProvince || ""
                  } ${propertyData.address?.zipCode || ""}`
                : [
                    propertyData.address?.unparsedAddress || "",
                    propertyData.address?.city || "",
                    propertyData.address?.stateOrProvince || "",
                    propertyData.address?.zipCode || "",
                  ]
                    .filter(Boolean)
                    .join(", ")}
            </p>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center gap-4">
                <p
                  className={`text-xl xl:text-3xl !font-extrabold ${styles.price}`}
                >
                  {(() => {
                    // RMLS Compliance for PortlandORSpeculo - show price range on details page
                    if (propertyData.mlsBoardCode === "PortlandORSpeculo") {
                      const listprice = propertyData.property?.listprice;
                      const listpricelow = propertyData.property?.listpricelow;

                      // If both exist and differ and neither is 0, show range
                      if (
                        listprice &&
                        listpricelow &&
                        parseFloat(listprice) !== parseFloat(listpricelow) &&
                        parseFloat(listpricelow) > 0
                      ) {
                        return `${formatPrice(
                          parseFloat(listpricelow),
                        )} - ${formatPrice(parseFloat(listprice))}`;
                      }

                      // Otherwise show single price (listprice if available, or listPriceLow)
                      const priceToDisplay = listprice
                        ? parseFloat(listprice)
                        : propertyData.listPriceLow
                          ? parseFloat(propertyData.listPriceLow)
                          : null;
                      return priceToDisplay ? formatPrice(priceToDisplay) : "-";
                    }

                    // For other MLS boards, use listPriceLow
                    return propertyData.listPriceLow
                      ? formatPrice(parseFloat(propertyData.listPriceLow))
                      : "Price not available";
                  })()}
                </p>
                <div className="flex items-center gap-2">
                  <span
                    className="px-3 py-1 lg:px-4 rounded-full text-white text-xs lg:text-sm"
                    style={{
                      backgroundColor: (() => {
                        const status =
                          propertyData.standardStatus?.toLowerCase();
                        if (
                          status === "active" ||
                          status === "active under contract"
                        )
                          return "#16a34a";
                        if (status === "pending") return "#E5A73B";
                        if (status === "closed") return "#ef4444";
                        return "#969696";
                      })(),
                    }}
                  >
                    {(() => {
                      const standardStatus =
                        propertyData.standardStatus || "Active";
                      const mlsStatus = propertyData.mlsstatus;
                      const mlsBoardCode = propertyData.mlsBoardCode;

                      // Format mlsStatus to add spaces (e.g., "BumpableBuyer" -> "Bumpable Buyer")
                      const formattedMlsStatus = mlsStatus
                        ? formatCamelCase(mlsStatus)
                        : "";

                      // RMLS Compliance: If PortlandORSpeculo and status is not Active, only show mlsStatus
                      if (
                        mlsBoardCode === "PortlandORSpeculo" &&
                        standardStatus.toLowerCase() !== "active"
                      ) {
                        return formattedMlsStatus || standardStatus;
                      }

                      // If both exist and are different, show "standardStatus - mlsStatus"
                      if (
                        mlsStatus &&
                        typeof mlsStatus === "string" &&
                        standardStatus.toLowerCase() !== mlsStatus.toLowerCase()
                      ) {
                        return `${standardStatus} - ${formattedMlsStatus}`;
                      }

                      // Otherwise just show mlsStatus if it exists, or standardStatus
                      return formattedMlsStatus || standardStatus;
                    })()}
                  </span>

                  {/* Special Property Indicators next to status */}
                  {propertyData.property?.isNewConstruction && (
                    <span className="px-3 py-1 text-xs lg:text-sm font-medium bg-blue-100 text-blue-800 rounded-full">
                      New Construction
                    </span>
                  )}
                  {(propertyData.property?.isRental ||
                    propertyData.property?.propertyType ===
                      "Residential Lease") && (
                    <span className="px-3 py-1 text-xs lg:text-sm font-medium bg-purple-100 text-purple-800 rounded-full">
                      Rental
                    </span>
                  )}
                </div>
              </div>
              <div>
                <SavePropertyButton
                  mlsNumber={propertyData.mlsNumber}
                  propertyId={listingKey}
                  mlsBoardCode={propertyData.mlsBoardCode}
                  address={{
                    full:
                      propertyData.address?.unparsedAddress ||
                      [city || "", state || ""].filter(Boolean).join(", ") ||
                      "",
                    city: propertyData.address?.city || "",
                    state: propertyData.address?.stateOrProvince || "",
                    zip: propertyData.address?.zipCode || "",
                  }}
                  details={{
                    bedrooms: propertyData.property?.bedroomsTotal || 0,
                    bathrooms: propertyData.property?.bathroomsTotal || 0,
                    squareFeet: parseFloat(
                      propertyData.property?.livingArea ||
                        propertyData.property?.lotSizeSquareFeet ||
                        "0",
                    ),
                    price: parseFloat(propertyData.listPriceLow || "0"),
                    listprice: propertyData.property?.listprice,
                    listpricelow: propertyData.property?.listpricelow,
                    internetAddressDisplayYN:
                      propertyData.internetAddressDisplayYN,
                    mustRemovePrimaryPhoto:
                      propertyData.displayRules?.mustRemovePrimaryPhoto,
                    primaryPhotoUrl: photos[0] || placeholderImage,
                    features: [
                      propertyData.property?.propertySubType,
                      propertyData.property?.hasPool && "Pool",
                      propertyData.property?.hasBasement && "Basement",
                      propertyData.property?.view && propertyData.property.view,
                      propertyData.property?.garageSpaces &&
                        `${propertyData.property.garageSpaces} Car Garage`,
                    ]
                      .flat(Infinity)
                      .filter((f) => f && typeof f === "string")
                      .map(String),
                    listingStatus: propertyData.standardStatus || "Active",
                    listingOffice:
                      propertyData.listingOffice?.name ||
                      propertyData.listingOffice?.ListOfficeName ||
                      "-",
                    mlsstatus: propertyData.mlsstatus,
                  }}
                  className="px-4 py-2 rounded-lg bg-white border transition-colors hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap"
                />
              </div>
            </div>
          </div>

          {propertyData.property?.propertyType === "Residential" ||
          propertyData.property?.propertyType === "Residential Lease" ||
          propertyData.property?.propertyType === "Residential Income" ? (
            <PropertyBasicInfo
              property={propertyData.property}
              pricePerSqFt={
                propertyData.property?.livingArea &&
                propertyData.listPriceLow &&
                parseFloat(propertyData.property.livingArea) > 0
                  ? parseFloat(propertyData.listPriceLow) /
                    parseFloat(propertyData.property.livingArea)
                  : undefined
              }
              formatPrice={formatPrice}
            />
          ) : null}

          <PropertyDetailsGrid
            propertyData={propertyData}
            features={features}
            formatPrice={formatPrice}
            primaryColor={primaryColor}
            teamMember={teamMember}
          />

          {/* Listing Courtesy - Bright MLS (shown below description, above compliance footer) */}
          {propertyData.mlsBoardCode === "Bright" && (
            <div className="pt-4 xl:pt-0">
              <div className="text-left lg:text-right">
                  <div className="sm:leading-[20px]">
                    <div className="flex flex-col sm:flex-row sm:items-center">
                      <span className={styles.propertyInfoLabel}>
                        Listing Courtesy of:
                      </span>{" "}
                      <span className={styles.propertyInfoValue}>
                        {propertyData.listingOffice?.name ||
                          propertyData.listingOffice?.ListOfficeName ||
                          ""}
                        {propertyData.listAgent?.listagentfullname && (
                          <> - {propertyData.listAgent.listagentfullname}</>
                        )}
                      </span>
                    </div>

                    {/* Agent contact info */}
                    <div className="flex flex-wrap gap-2">
                      {propertyData.listAgent?.listagentemail && (
                        <a
                          href={`mailto:${propertyData.listAgent.listagentemail}`}
                          className={`${styles.propertyInfoValue} underline`}
                        >
                          {propertyData.listAgent.listagentemail}
                        </a>
                      )}
                      {(() => {
                        const preferredPhone =
                          propertyData.listAgent?.listagentprefferedphone;
                        const attributionContact =
                          propertyData.listAgent?.attributioncontact;
                        const officePhone =
                          propertyData.listAgent?.listagentofficephone;
                        const phoneToDisplay =
                          preferredPhone || attributionContact || officePhone;
                        return phoneToDisplay ? (
                          <a
                            href={`tel:${phoneToDisplay}`}
                            className={`${styles.propertyInfoValue} underline`}
                          >
                            {formatPhone(phoneToDisplay)}
                          </a>
                        ) : null;
                      })()}
                    </div>
                  </div>

                  {/* Buyer Agent Information */}
                  {(propertyData.standardStatus?.toLowerCase() === "closed" ||
                    propertyData.standardStatus?.toLowerCase() === "sold") &&
                    propertyData.buyerAgent &&
                    (propertyData.buyerAgent.buyeragentfullname ||
                      propertyData.buyerAgent.buyeragentmlsid ||
                      propertyData.buyerAgent.buyeragentofficephone) && (
                      <div className="text-sm md:text-base">
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className={styles.propertyInfoLabel}>
                            Bought with:
                          </span>{" "}
                          <span className={styles.propertyInfoValue}>
                            {propertyData.buyerAgent.buyeragentfullname}
                            {propertyData.buyerAgent.buyerofficename &&
                              ` - ${propertyData.buyerAgent.buyerofficename}`}
                          </span>
                        </div>
                      </div>
                    )}
              </div>
            </div>
          )}

          {/* Compliance Footer */}
          <ComplianceFooter
            properties={[
              {
                mlsNumber: propertyData.mlsNumber,
                mlsBoardCode: propertyData.mlsBoardCode,
              },
            ]}
            timestamp={
              propertyData.property?.modificationTimestamp || timestamp
            }
          />
        </ResponsiveContainer>
      </div>

      {/* Squeeze page overlay */}
      {showSqueezePage && (
        <SqueezePageOverlay
          onClose={handleSqueezePageClose}
          onSuccess={handleSqueezePageSuccess}
          propertyId={listingKey}
          propertyDetails={{
            address: propertyData?.address?.unparsedAddress || "",
            city: propertyData?.address?.city || "",
            state: propertyData?.address?.stateOrProvince || "",
            zipCode: propertyData?.address?.zipCode || "",
            propertyType: propertyData?.property?.propertyType || "",
            beds: propertyData?.property?.bedroomsTotal,
            baths: propertyData?.property?.bathroomsTotal,
            sqft: propertyData?.property?.livingArea
              ? parseFloat(propertyData.property.livingArea)
              : undefined,
            listPrice: propertyData?.listPriceLow
              ? parseFloat(propertyData.listPriceLow)
              : undefined,
          }}
          isConfirmationMode={
            !!urlParams.contact_id && urlParams.utm_source === "email"
          }
          hasMissingContact={hasMissingContact}
          closeOnlyModal={true}
        />
      )}

      {/* Lightbox Gallery */}
      {showLightbox && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") prevPhoto();
            if (e.key === "ArrowRight") nextPhoto();
            if (e.key === "Escape") setShowLightbox(false);
          }}
        >
          <button
            onClick={() => setShowLightbox(false)}
            className="!bg-transparent absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-50"
            aria-label="Close gallery"
          >
            <X size={32} />
          </button>

          {/* Large navigation buttons on sides */}
          <button
            onClick={prevPhoto}
            className="absolute left-2 md:left-6 top-1/2 transform -translate-y-1/2 w-12 h-12 md:w-16 md:h-16 rounded-full bg-black/40 text-white hover:bg-black/60 flex items-center justify-center transition-all z-40"
            aria-label="Previous photo"
          >
            <ChevronLeft size={32} />
          </button>

          <button
            onClick={nextPhoto}
            className="absolute right-2 md:right-6 top-1/2 transform -translate-y-1/2 w-12 h-12 md:w-16 md:h-16 rounded-full bg-black/40 text-white hover:bg-black/60 flex items-center justify-center transition-all z-40"
            aria-label="Next photo"
          >
            <ChevronRight size={32} />
          </button>

          <div className="w-full h-full flex flex-col items-center justify-center p-4">
            <img
              src={getOptimizedImageUrl(photos[currentPhotoIndex], 1600)}
              alt={`${propertyType} in ${city} - Photo ${
                currentPhotoIndex + 1
              }`}
              className="max-h-[75vh] max-w-[90vw] object-contain"
            />

            <div className="flex items-center justify-center mt-2 gap-4 bg-black/30 px-4 py-2 rounded-full">
              <span className="text-white font-medium">
                {currentPhotoIndex + 1} / {photos.length}
              </span>
            </div>

            {/* Improved thumbnail strip */}
            <div className="fixed bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm py-4 px-8 overflow-x-auto">
              <div className="flex justify-start gap-2 max-w-[1200px] mx-auto pl-8">
                {photos.map((photo, index) => (
                  <div
                    key={index}
                    onClick={() => setCurrentPhotoIndex(index)}
                    className={`
                      w-20 h-20 md:w-24 md:h-24 flex-shrink-0 cursor-pointer transition-all
                      p-1 rounded-lg
                      ${
                        currentPhotoIndex === index
                          ? "opacity-100 bg-white/30 ring-2 ring-white scale-110 z-10"
                          : "opacity-70 hover:opacity-100 hover:bg-white/20 hover:scale-105"
                      }
                    `}
                  >
                    <img
                      src={getOptimizedImageUrl(photo, 150)}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover object-bottom rounded"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetail;
